import React from 'react';
import {
  Database,
  MessageCircle,
  CreditCard,
  UserCog,
  HardDrive,
  Cloud,
  FileStack,
  CalendarClock,
} from 'lucide-react';
import type { AdminOrgUsage, AdminTenantUsageSummary } from '../api';
import { smsRangeCostInr, smsUnitCost } from '../lib/smsBilling';
import { StatCard } from './shared';

function StorageBar({
  label,
  usedMb,
  limitMb,
  percent,
  full,
  tone,
}: {
  label: string;
  usedMb: number;
  limitMb: number;
  percent: number;
  full: boolean;
  tone: 'emerald' | 'violet';
}) {
  const bar = tone === 'emerald' ? 'bg-emerald-600' : 'bg-violet-600';
  return (
    <div className="rounded-lg border border-zinc-100 bg-zinc-50/80 p-3">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="font-medium text-zinc-800">{label}</span>
        <span className="tabular-nums text-zinc-600">
          <span className="font-semibold text-zinc-900">{usedMb}</span>
          <span className="text-zinc-400"> / {limitMb} MB</span>
          <span className="ml-1 text-xs text-zinc-400">({percent}%)</span>
        </span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-zinc-200">
        <div
          className={`h-full rounded-full transition-all ${full ? 'bg-rose-500' : bar}`}
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
    </div>
  );
}

function bytesToMb(bytes: number) {
  return Number((bytes / (1024 * 1024)).toFixed(3));
}

function formatPlanEnd(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { dateStyle: 'medium' });
}

function planEndHint(iso: string | null | undefined): string {
  if (!iso) return 'Not set';
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  if (days < 0) return `Expired ${Math.abs(days)}d ago`;
  if (days === 0) return 'Today';
  return `${days}d left`;
}

export function TenantUsagePanel({ detail }: { detail: AdminOrgUsage }) {
  const s = detail.storage;
  const u = detail.usage;
  const org = detail.organization;
  const expiresAt = org.subscriptionExpiresAt;
  const daysLeft = expiresAt ? Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000) : null;
  const endTone =
    daysLeft == null ? 'text-zinc-500' : daysLeft < 0 ? 'text-rose-700' : daysLeft <= 7 ? 'text-amber-800' : 'text-emerald-800';

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard label="Plan" value={detail.plan?.name || org.planId || '—'} icon={<CreditCard className="h-4 w-4" />} />
        <StatCard label="Billing" value={org.subscriptionStatus} icon={<CalendarClock className="h-4 w-4" />} />
        <StatCard
          label="Plan ends"
          value={formatPlanEnd(expiresAt)}
          hint={planEndHint(expiresAt)}
          icon={<CalendarClock className={`h-4 w-4 ${endTone}`} />}
        />
        <StatCard label="Workspace admins" value={u.admins} icon={<UserCog className="h-4 w-4" />} />
        <StatCard label="Total records" value={u.records.workspaceData + u.records.finance + u.records.handloans + u.records.emiPayments} icon={<FileStack className="h-4 w-4" />} />
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Storage quotas (from plan)</p>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <StorageBar
            label="Media uploads (Cloudinary)"
            usedMb={s.mediaStorageUsedMb}
            limitMb={s.mediaStorageLimitMb}
            percent={s.mediaStoragePercentUsed}
            full={s.mediaStorageFull}
            tone="emerald"
          />
          <StorageBar
            label="Database (MongoDB)"
            usedMb={s.databaseStorageUsedMb}
            limitMb={s.databaseStorageLimitMb}
            percent={s.databaseStoragePercentUsed}
            full={s.databaseStorageFull}
            tone="violet"
          />
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">MongoDB by collection</p>
        <div className="mt-2 overflow-x-auto rounded-lg border border-zinc-200">
          <table className="w-full min-w-[480px] text-left text-sm">
            <thead className="bg-zinc-50 text-[11px] font-bold uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="px-3 py-2">Collection</th>
                <th className="px-3 py-2 text-right">Documents</th>
                <th className="px-3 py-2 text-right">Est. size</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {[
                { key: 'Workspace KV', docs: u.records.workspaceData, bytes: u.mongoStorage.byCollectionBytes.workspaceData },
                { key: 'Finance records', docs: u.records.finance, bytes: u.mongoStorage.byCollectionBytes.finance },
                { key: 'Hand loans', docs: u.records.handloans, bytes: u.mongoStorage.byCollectionBytes.handloans },
                { key: 'EMI payments', docs: u.records.emiPayments, bytes: u.mongoStorage.byCollectionBytes.emiPayments },
              ].map((row) => (
                <tr key={row.key}>
                  <td className="px-3 py-2 font-medium text-zinc-800">{row.key}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.docs}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-600">{bytesToMb(row.bytes)} MB</td>
                </tr>
              ))}
              <tr className="bg-zinc-50 font-semibold">
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {u.records.workspaceData + u.records.finance + u.records.handloans + u.records.emiPayments}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{u.mongoStorage.estimatedMb.toFixed(3)} MB</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">SMS billing meter</p>
        {(u.sms ?? u.whatsapp).period?.filtered ? (
          <p className="mt-1 text-xs text-teal-700">
            Filtered {(u.sms ?? u.whatsapp).period?.fromYmd || '…'} → {(u.sms ?? u.whatsapp).period?.toYmd || '…'} (matches date bar)
          </p>
        ) : (
          <p className="mt-1 text-xs text-zinc-500">All-time SMS (set a date range in the top bar to filter)</p>
        )}
        <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {(() => {
            const sms = u.sms ?? u.whatsapp;
            const sent = sms.periodSent ?? sms.totalSent ?? 0;
            const unit = smsUnitCost(sms.unitCostInr);
            const cost = smsRangeCostInr(sent, unit, sms.periodCostInr ?? sms.apiCostInr);
            return (
              <>
                <StatCard
                  label="In selected range"
                  value={sent}
                  icon={<MessageCircle className="h-4 w-4 text-emerald-600" />}
                />
                <StatCard label="Last 7 days" value={sms.last7d} icon={<MessageCircle className="h-4 w-4" />} />
                <StatCard label="Last 30 days" value={sms.last30d} icon={<MessageCircle className="h-4 w-4" />} />
                <StatCard
                  label="Range cost (INR)"
                  value={cost.toFixed(2)}
                  hint={`₹${unit}/SMS × ${sent}`}
                  icon={<CreditCard className="h-4 w-4" />}
                />
              </>
            );
          })()}
        </div>
        <p className="mt-2 text-xs text-zinc-500">
          Triggers — manual: {(u.sms ?? u.whatsapp).byTrigger.manual} · schedule: {(u.sms ?? u.whatsapp).byTrigger.schedule} · cron:{' '}
          {(u.sms ?? u.whatsapp).byTrigger.cron}
          {(u.sms ?? u.whatsapp).failed ? ` · Failed: ${(u.sms ?? u.whatsapp).failed}` : ''}
          {(u.sms ?? u.whatsapp).lastSentAt ? ` · Last in range ${new Date((u.sms ?? u.whatsapp).lastSentAt!).toLocaleString('en-IN')}` : ''}
        </p>
        {(u.sms ?? u.whatsapp).daily?.length ? (
          <div className="mt-3 max-h-48 overflow-auto rounded-lg border border-zinc-200">
            <p className="sticky top-0 border-b border-zinc-100 bg-zinc-50 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-zinc-500">
              Daily SMS ({(u.sms ?? u.whatsapp).daily!.length} days · sort {(u.sms ?? u.whatsapp).sort || 'date_desc'})
            </p>
            <table className="w-full min-w-[320px] text-left text-xs">
              <thead className="bg-white text-[10px] font-bold uppercase tracking-wide text-zinc-500">
                <tr>
                  <th className="px-2 py-1.5">Date</th>
                  <th className="px-2 py-1.5 text-right">Sent</th>
                  <th className="px-2 py-1.5 text-right">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {(u.sms ?? u.whatsapp).daily!.map((row) => (
                  <tr key={row.date}>
                    <td className="px-2 py-1.5 tabular-nums">{row.date}</td>
                    <td className="px-2 py-1.5 text-right font-semibold tabular-nums">{row.count}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      ₹
                      {smsRangeCostInr(
                        row.count,
                        (u.sms ?? u.whatsapp).unitCostInr,
                        row.costInr
                      ).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {(u.sms ?? u.whatsapp).recent?.length ? (
          <div className="mt-3 max-h-56 overflow-auto rounded-lg border border-zinc-200">
            <table className="w-full min-w-[640px] text-left text-xs">
              <thead className="sticky top-0 bg-zinc-50 text-[10px] font-bold uppercase tracking-wide text-zinc-500">
                <tr>
                  <th className="px-2 py-1.5">When</th>
                  <th className="px-2 py-1.5">Status</th>
                  <th className="px-2 py-1.5">Trigger</th>
                  <th className="px-2 py-1.5">Customer</th>
                  <th className="px-2 py-1.5">Ref ID</th>
                  <th className="px-2 py-1.5">Gateway</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {(u.sms ?? u.whatsapp).recent!.slice(0, 40).map((row) => (
                  <tr key={row.id}>
                    <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">
                      {row.at ? new Date(row.at).toLocaleString('en-IN') : '—'}
                    </td>
                    <td className={`px-2 py-1.5 font-semibold ${row.status === 'sent' ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {row.status}
                    </td>
                    <td className="px-2 py-1.5">{row.trigger || '—'}</td>
                    <td className="px-2 py-1.5">{row.customerName || '—'}</td>
                    <td className="px-2 py-1.5 max-w-[140px] truncate" title={row.refId}>
                      {row.refId || '—'}
                    </td>
                    <td className="px-2 py-1.5 max-w-[100px] truncate text-zinc-500" title={row.requestId}>
                      {row.requestId || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mt-3 text-xs text-zinc-500">No SMS in this date range for this shop.</p>
        )}
      </div>
    </div>
  );
}

export function TenantUsageSummaryRow({
  t,
  onDetail,
  loading,
}: {
  t: AdminTenantUsageSummary;
  onDetail?: () => void;
  loading?: boolean;
}) {
  const s = t.storage;
  return (
    <tr className="hover:bg-zinc-50/90">
      <td className="px-3 py-3">
        <p className="font-semibold text-zinc-900">{t.name}</p>
        <p className="text-[11px] text-zinc-500">{t.adminEmail}</p>
      </td>
      <td className="px-3 py-3 text-sm">{t.planName || t.planId || '—'}</td>
      <td className="px-3 py-3">
        <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-700">{t.subscriptionStatus}</span>
      </td>
      <td className="px-3 py-3">
        <p className="text-xs font-medium text-zinc-800">{formatPlanEnd(t.subscriptionExpiresAt)}</p>
        <p className="text-[11px] text-zinc-500">{planEndHint(t.subscriptionExpiresAt)}</p>
      </td>
      <td className="px-3 py-3">
        <div className="min-w-[120px]">
          <p className="text-xs tabular-nums text-zinc-700">
            {s.mediaStorageUsedMb} / {s.mediaStorageLimitMb} MB
          </p>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-zinc-200">
            <div
              className={`h-full ${s.mediaStorageFull ? 'bg-rose-500' : 'bg-emerald-600'}`}
              style={{ width: `${Math.min(100, s.mediaStoragePercentUsed)}%` }}
            />
          </div>
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="min-w-[120px]">
          <p className="text-xs tabular-nums text-zinc-700">
            {s.databaseStorageUsedMb} / {s.databaseStorageLimitMb} MB
          </p>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-zinc-200">
            <div
              className={`h-full ${s.databaseStorageFull ? 'bg-rose-500' : 'bg-violet-600'}`}
              style={{ width: `${Math.min(100, s.databaseStoragePercentUsed)}%` }}
            />
          </div>
        </div>
      </td>
      <td className="px-3 py-3 text-right tabular-nums text-sm">{t.records.total}</td>
      <td className="px-3 py-3 text-right tabular-nums text-sm">{t.smsMessagesInPeriod ?? t.whatsappMessagesInPeriod}</td>
      <td className="px-3 py-3 text-right tabular-nums text-xs text-zinc-600">
        ₹
        {smsRangeCostInr(
          t.smsMessagesInPeriod ?? t.whatsappMessagesInPeriod ?? 0,
          t.smsUnitCostInr,
          t.smsApiCostInr ?? t.whatsappApiCostInr
        ).toFixed(2)}
      </td>
      <td className="px-3 py-3 text-right">
        {onDetail ? (
          <button
            type="button"
            onClick={onDetail}
            disabled={loading}
            className="rounded-lg border border-zinc-200 px-2 py-1 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-60"
          >
            {loading ? '…' : 'Details'}
          </button>
        ) : null}
      </td>
    </tr>
  );
}

export function UsageMiniStats({ tenants }: { tenants: AdminTenantUsageSummary[] }) {
  const totalMedia = tenants.reduce((s, t) => s + t.storage.mediaStorageUsedMb, 0);
  const totalDb = tenants.reduce((s, t) => s + t.storage.databaseStorageUsedMb, 0);
  const fullMedia = tenants.filter((t) => t.storage.mediaStorageFull).length;
  const fullDb = tenants.filter((t) => t.storage.databaseStorageFull).length;
  const sms = tenants.reduce((s, t) => s + (t.smsMessagesInPeriod ?? t.whatsappMessagesInPeriod), 0);
  const smsCost = tenants.reduce(
    (s, t) =>
      s +
      smsRangeCostInr(
        t.smsMessagesInPeriod ?? t.whatsappMessagesInPeriod ?? 0,
        t.smsUnitCostInr,
        t.smsApiCostInr ?? t.whatsappApiCostInr
      ),
    0
  );
  const unit = smsUnitCost(tenants[0]?.smsUnitCostInr);

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard label="Tenants tracked" value={tenants.length} icon={<Database className="h-4 w-4" />} />
      <StatCard label="Total DB used" value={`${totalDb.toFixed(1)} MB`} icon={<HardDrive className="h-4 w-4 text-violet-600" />} hint={`${fullDb} at limit`} />
      <StatCard label="Total media used" value={`${totalMedia.toFixed(1)} MB`} icon={<Cloud className="h-4 w-4 text-emerald-600" />} hint={`${fullMedia} at limit`} />
      <StatCard label="SMS sent (period)" value={sms} icon={<MessageCircle className="h-4 w-4" />} hint={`₹${smsCost.toFixed(2)} @ ₹${unit}/SMS`} />
    </div>
  );
}
